import { findValueInEnum, parseBoolean } from "@saleor/misc";

import { TableCellHeaderArrowDirection } from "../components/TableCellHeader";
import { Sort } from "../types";
import { OrderDirection } from "../types/globalTypes";
import { getQueryParamString } from "./urls";

export function getSortUrlVariables<TSortKey extends string>(
  field: TSortKey,
  params: Sort<TSortKey>
): Sort<TSortKey> {
  if (field === params.sort) {
    return {
      asc: !params.asc,
      sort: field
    };
  }

  return {
    asc: true,
    sort: field
  };
}

export function getOrderDirection(asc: boolean): OrderDirection {
  return asc ? OrderDirection.ASC : OrderDirection.DESC;
}

export function getArrowDirection(asc: boolean): TableCellHeaderArrowDirection {
  return asc ? "asc" : "desc";
}

export function getSortParams<
  TParams extends Sort<TFields>,
  TFields extends string
>(params: TParams): Sort<TFields> {
  return {
    asc: params.asc,
    sort: params.sort
  };
}

export function asSortParams<
  TParams extends Record<string, unknown>,
  TFields extends Record<any, string>
>(
  params: TParams,
  fields: TFields,
  defaultField?: keyof TFields,
  defaultOrder?: boolean
): TParams & Sort<keyof TFields & string> {
  const ascStr = getQueryParamString(
    params.asc as Parameters<typeof getQueryParamString>[0]
  );
  const sortStr = getQueryParamString(
    params.sort as Parameters<typeof getQueryParamString>[0]
  );
  return {
    ...params,
    asc: parseBoolean(ascStr, defaultOrder === undefined ? true : defaultOrder),
    sort: sortStr
      ? findValueInEnum(sortStr, fields)
      : (defaultField as string) || "name"
  } as TParams & Sort<keyof TFields & string>;
}

interface SortingInput<T extends string> {
  direction: OrderDirection;
  field: T;
}
type GetSortQueryField<TUrlField extends string, TSortField extends string> = (
  sort: TUrlField
) => TSortField;
type GetSortQueryVariables<
  TSortField extends string,
  TParams extends Record<any, any>
> = (params: TParams) => SortingInput<TSortField>;
export function createGetSortQueryVariables<
  TUrlField extends string,
  TSortField extends string,
  TParams extends Record<any, any>
>(
  getSortQueryField: GetSortQueryField<TUrlField, TSortField>
): GetSortQueryVariables<TSortField, TParams> {
  return (params: TParams) => {
    const field = getSortQueryField(params.sort);

    if (!!field) {
      return {
        direction: getOrderDirection(params.asc),
        field
      };
    }

    return undefined;
  };
}
